import { memo, useState } from "react";
import { VOICE_SUMMARY_FILTER } from "../utilities/constants";
import "../styles/voicemessagesummary.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Returns "N% of Total" when total > 0, otherwise "—". */
function calcPct(value, total) {
  return total > 0 ? `${((value / total) * 100).toFixed(0)}% of Total` : "—";
}

/** Maps raw summary API data to flat, named values used by the tiles. */
function buildSummaryData(summary) {
  const totalAttempts = summary?.totalAttempts ?? 0;
  return {
    intendedRecipients: totalAttempts,
    actualCalls: totalAttempts,
    answeredCalls: summary?.answered ?? 0,
    unansweredCalls: summary?.unanswered ?? 0,
    human: summary?.human ?? 0,
    machine: summary?.machine ?? 0,
    fax: summary?.fax ?? 0,
    unknownDevice: summary?.unknown ?? 0,
  };
}

/** Builds the ordered tile descriptor array from derived summary data. */
function buildTiles(d) {
  const {
    intendedRecipients,
    actualCalls,
    answeredCalls,
    unansweredCalls,
    human,
    machine,
    fax,
    unknownDevice,
  } = d;

  return [
    {
      title: "Intended Recipients",
      filterKey: VOICE_SUMMARY_FILTER.ALL_INTENDED,
      value: intendedRecipients,
      percentage: calcPct(intendedRecipients, actualCalls),
      color: "var(--fill-success-default, #43A35C)",
      showPercentage: false,
    },
    {
      title: "Actual Calls",
      filterKey: VOICE_SUMMARY_FILTER.ALL_ACTUAL,
      value: actualCalls,
      percentage: calcPct(actualCalls, actualCalls),
      color: "var(--stroke-button-success-default, #43A35C)",
      showPercentage: false,
    },
    {
      title: "Answered Calls",
      filterKey: VOICE_SUMMARY_FILTER.ANSWERED,
      value: answeredCalls,
      percentage: calcPct(answeredCalls, actualCalls),
      color: "var(--stroke-button-primary-default, #1863CC)",
      showPercentage: true,
    },
    {
      title: "Unanswered Calls",
      filterKey: VOICE_SUMMARY_FILTER.UNANSWERED,
      value: unansweredCalls,
      percentage: calcPct(unansweredCalls, actualCalls),
      color: "var(--fill-alert-default, #E67337)",
      showPercentage: true,
      percentageColor: "var(--text-base-primary, #363636)",
    },
    {
      title: "Human",
      filterKey: VOICE_SUMMARY_FILTER.HUMAN,
      value: human,
      percentage: calcPct(human, answeredCalls),
      color: "#867EC4",
      showPercentage: false,
    },
    {
      title: "Machine",
      filterKey: VOICE_SUMMARY_FILTER.MACHINE,
      value: machine,
      percentage: calcPct(machine, answeredCalls),
      color: "#867EC4",
      showPercentage: false,
    },
    {
      title: "Fax",
      filterKey: VOICE_SUMMARY_FILTER.FAX,
      value: fax,
      percentage: calcPct(fax, answeredCalls),
      color: "#867EC4",
      showPercentage: false,
    },
    {
      title: "Unknown Device",
      filterKey: VOICE_SUMMARY_FILTER.UNKNOWN,
      value: unknownDevice,
      percentage: calcPct(unknownDevice, answeredCalls),
      color: "#867EC4",
      showPercentage: false,
    },
  ];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryTile({ tile, isSelected, onClick }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") onClick();
  };

  return (
    <div
      className={`summary-tile-wrapper${isSelected ? " selected" : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
      aria-pressed={isSelected}
    >
      <div className="summary-tile">
        <div className="summary-tile-title">{tile.title}</div>
        <div className="summary-tile-body">
          <div className="summary-tile-value" style={{ color: tile.color }}>
            {tile.value}
          </div>
          {tile.percentage && (
            <div
              className={
                tile.showPercentage
                  ? "summary-tile-percentage"
                  : "summary-tile-percentage-hidden"
              }
              style={{ color: tile.percentageColor }}
            >
              {tile.percentage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function VoiceMessageSummary({ summary, onFilterChange }) {
  const [selectedTile, setSelectedTile] = useState(null);

  const handleTileClick = (filterKey) => {
    setSelectedTile(filterKey);
    if (onFilterChange) {
      // ALL_ACTUAL maps back to ALL_INTENDED (0) so the table shows all rows
      const status =
        filterKey === VOICE_SUMMARY_FILTER.ALL_ACTUAL
          ? VOICE_SUMMARY_FILTER.ALL_INTENDED
          : filterKey;
      onFilterChange(status);
    }
  };

  const tiles = buildTiles(buildSummaryData(summary));

  return (
    <div className="voice-summary-container">
      {tiles.map((tile) => (
        <SummaryTile
          key={tile.filterKey}
          tile={tile}
          isSelected={selectedTile === tile.filterKey}
          onClick={() => handleTileClick(tile.filterKey)}
        />
      ))}
    </div>
  );
}

export default memo(VoiceMessageSummary);
